import { useForm } from "@inertiajs/react";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileText,
  Upload,
} from "lucide-react";
import { useRef, useState } from "react";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";

interface ValidationError {
  type: string;
  message: string;
  field_path?: string;
  line_number?: number;
  context_lines?: Array<{
    number: number;
    content: string;
    is_error: boolean;
  }>;
}

interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  yaml_content?: string;
  error_count: number;
}

interface Props {
  validation_result?: ValidationResult;
}

export default function Index({ validation_result }: Props) {
  const [inputMode, setInputMode] = useState<"text" | "file">("text");
  const [fileName, setFileName] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, setData, post, processing } = useForm({
    yaml_content: "",
    file: null as File | null,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post("/tools/yaml-validation");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setData("file", file);
      setFileName(file.name);
      setInputMode("file");
    }
  };

  const handleTextChange = (value: string) => {
    setData("yaml_content", value);
    if (inputMode === "file") {
      setInputMode("text");
      setFileName("");
      setData("file", null);
    }
  };

  const clearForm = () => {
    setData({ yaml_content: "", file: null });
    setFileName("");
    setInputMode("text");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const downloadExample = () => {
    window.location.href = "/tools/yaml-validation/example";
  };

  return (
    <div className="min-h-screen bg-muted p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold tracking-tight">
            YAML Validation Tool
          </h1>
          <p className="text-muted-foreground text-lg">
            Validate your pillar insight YAML files before importing
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Upload or Paste YAML</CardTitle>
              <CardDescription>
                Upload a YAML file or paste the content directly
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="file-upload">Upload File</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {fileName || "Choose File"}
                    </Button>
                    <input
                      ref={fileInputRef}
                      id="file-upload"
                      type="file"
                      accept=".yml,.yaml"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                  {fileName && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      {fileName}
                    </p>
                  )}
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      Or
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="yaml-content">Paste YAML Content</Label>
                  <Textarea
                    id="yaml-content"
                    placeholder="Paste your YAML content here..."
                    value={data.yaml_content}
                    onChange={(e) => handleTextChange(e.target.value)}
                    rows={15}
                    className="font-mono text-sm"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={processing}
                    className="flex-1"
                  >
                    {processing ? "Validating..." : "Validate YAML"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={clearForm}
                    disabled={processing}
                  >
                    Clear
                  </Button>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={downloadExample}
                  className="w-full"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Example YAML
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Validation Results</CardTitle>
              <CardDescription>
                {validation_result
                  ? validation_result.valid
                    ? "Your YAML is valid!"
                    : `Found ${validation_result.error_count} error${validation_result.error_count !== 1 ? "s" : ""}`
                  : "Submit your YAML to see validation results"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!validation_result && (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mb-4 opacity-50" />
                  <p>No validation results yet</p>
                </div>
              )}

              {validation_result?.valid && (
                <div className="rounded-lg border border-border bg-muted p-6">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-8 w-8 text-chart-6" />
                    <div>
                      <h3 className="font-semibold text-foreground">
                        Valid YAML
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Your YAML file passed all validation checks and is ready
                        to import.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {validation_result?.errors &&
                validation_result.errors.length > 0 && (
                  <div className="space-y-4">
                    {validation_result.errors.map((error, index) => (
                      <div
                        key={index}
                        className="rounded-lg border border-destructive/50 bg-destructive/5 p-4"
                      >
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={
                                  error.type === "syntax"
                                    ? "destructive"
                                    : "secondary"
                                }
                              >
                                {error.type === "syntax"
                                  ? "Syntax Error"
                                  : "Validation Error"}
                              </Badge>
                              {error.line_number && (
                                <Badge variant="outline">
                                  Line {error.line_number}
                                </Badge>
                              )}
                            </div>

                            <p className="text-sm font-medium text-foreground">
                              {error.message}
                            </p>

                            {error.field_path && (
                              <p className="text-xs text-muted-foreground">
                                Field:{" "}
                                <code className="font-mono">
                                  {error.field_path}
                                </code>
                              </p>
                            )}

                            {error.context_lines &&
                              error.context_lines.length > 0 && (
                                <div className="mt-3">
                                  <p className="text-xs font-semibold text-foreground mb-2">
                                    Context:
                                  </p>
                                  <div className="rounded bg-background border border-border overflow-hidden">
                                    <pre className="text-xs p-3 overflow-x-auto">
                                      {error.context_lines.map((line) => (
                                        <div
                                          key={line.number}
                                          className={`${
                                            line.is_error
                                              ? "bg-destructive/10 font-semibold"
                                              : ""
                                          }`}
                                        >
                                          <span className="text-muted-foreground inline-block w-10 select-none">
                                            {line.number}
                                          </span>
                                          <span className="ml-2">
                                            {line.content}
                                          </span>
                                        </div>
                                      ))}
                                    </pre>
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>How to Use</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none">
            <ol>
              <li>
                Upload a YAML file or paste your YAML content directly into the
                text area
              </li>
              <li>Click "Validate YAML" to check your file</li>
              <li>
                Review any errors with line numbers and context to fix issues
              </li>
              <li>
                Once validation passes, your YAML is ready to import through the
                admin panel
              </li>
            </ol>
            <p className="text-sm text-muted-foreground mt-4">
              <strong>Note:</strong> This tool only validates the structure of
              your YAML file. No data is saved or imported during validation.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
